// src/services/strategyService.js
import axios from 'axios';
import API_URLS from '../config/api';

/**
 * Strategy Service - Handles all strategy-related API calls
 * Backend app: alerts
 * Base URL: /alerts/api/...
 */

// Helper function to get CSRF token from cookies
const getCookie = (name) => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Axios request config with CSRF
const getRequestConfig = () => ({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-CSRFToken': getCookie('csrftoken'),
  },
});

// ==================== DASHBOARD ====================

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} Dashboard stats including active strategies, signals, success rate
 */
export const getDashboardStats = async () => {
  try {
    const response = await axios.get(
      API_URLS.DASHBOARD_STATS,
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch dashboard stats',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get strategy performance metrics
 * @param {string} timeRange - Time range: 'today', 'week', 'month'
 * @returns {Promise<Object>} Performance metrics
 */
export const getStrategyPerformance = async (timeRange = 'week') => {
  try {
    const response = await axios.get(
      API_URLS.DASHBOARD_PERFORMANCE,
      {
        params: { time_range: timeRange },
        ...getRequestConfig(),
      }
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching strategy performance:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch performance data',
      error: error.response?.data || error.message,
    };
  }
};

// ==================== STRATEGY MANAGEMENT ====================

/**
 * Setup/create a new strategy or update existing one
 * @param {Object} strategyData - Strategy configuration
 * @param {string} strategyData.strategy_id - Unique strategy identifier
 * @param {string} strategyData.symbol - Trading symbol (e.g., 'BTCUSD')
 * @param {string} strategyData.timeframe - Timeframe (e.g., '1m', '5m')
 * @param {Array} strategyData.conditions - Array of condition objects
 * @param {string} strategyData.entry_logic - 'all' or 'any'
 * @param {Object} strategyData.alert_config - Alert configuration
 * @param {string} strategyData.webhook_url - Optional webhook URL
 * @returns {Promise<Object>} Created/updated strategy
 */
export const setupStrategy = async (strategyData) => {
  try {
    const response = await axios.post(
      API_URLS.STRATEGY_SETUP,
      strategyData,
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error setting up strategy:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to setup strategy',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * List all strategies
 * @param {Object} filters - Optional filters
 * @param {boolean} filters.is_active - Filter by active status
 * @returns {Promise<Object>} List of strategies
 */
export const listStrategies = async (filters = {}) => {
  try {
    const response = await axios.get(
      API_URLS.STRATEGY_LIST,
      {
        params: filters,
        ...getRequestConfig(),
      }
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error listing strategies:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch strategies',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get strategy state and configuration
 * @param {string} strategyId - Strategy ID
 * @returns {Promise<Object>} Strategy state and configuration
 */
export const getStrategyState = async (strategyId) => {
  try {
    const response = await axios.get(
      API_URLS.STRATEGY_STATE(strategyId),
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching strategy state:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch strategy state',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Reset strategy state
 * @param {string} strategyId - Strategy ID to reset
 * @returns {Promise<Object>} Reset confirmation
 */
export const resetStrategy = async (strategyId) => {
  try {
    const response = await axios.post(
      API_URLS.STRATEGY_RESET,
      { strategy_id: strategyId },
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error resetting strategy:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to reset strategy',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Delete strategy and all related data
 * @param {string} strategyId - Strategy ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteStrategy = async (strategyId) => {
  try {
    const response = await axios.delete(
      API_URLS.STRATEGY_DELETE(strategyId),
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error deleting strategy:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to delete strategy',
      error: error.response?.data || error.message,
    };
  }
};

// ==================== SIGNAL PROCESSING ====================

/**
 * Send signal alert to be processed by strategy
 * @param {Object} signalData - Signal data
 * @param {string} signalData.strategy_id - Strategy ID
 * @param {Object} signalData.signals - Signal data (old or new format)
 * @returns {Promise<Object>} Processing results
 */
export const sendSignalAlert = async (signalData) => {
  try {
    const response = await axios.post(
      API_URLS.SIGNAL_ALERT,
      signalData,
      getRequestConfig()
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error sending signal alert:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to process signal',
      error: error.response?.data || error.message,
    };
  }
};

// ==================== HISTORY AND LOGS ====================

/**
 * Get detected signals history for a strategy
 * @param {string} strategyId - Strategy ID
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date filter (ISO format)
 * @param {string} params.end_date - End date filter (ISO format)
 * @param {string} params.condition_name - Filter by condition name
 * @param {boolean} params.is_entry_signal - Filter by entry signal status
 * @param {number} params.limit - Limit results (default: 100)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @returns {Promise<Object>} Detected signals history
 */
export const getDetectedSignals = async (strategyId, params = {}) => {
  try {
    const response = await axios.get(
      API_URLS.SIGNALS_HISTORY(strategyId),
      {
        params: params,
        ...getRequestConfig(),
      }
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching detected signals:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch signals history',
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get signal events log for a strategy
 * @param {string} strategyId - Strategy ID
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date filter (ISO format)
 * @param {string} params.end_date - End date filter (ISO format)
 * @param {number} params.limit - Limit results (default: 100)
 * @param {number} params.offset - Offset for pagination (default: 0)
 * @returns {Promise<Object>} Signal events log
 */
export const getSignalEvents = async (strategyId, params = {}) => {
  try {
    const response = await axios.get(
      API_URLS.SIGNAL_EVENTS(strategyId),
      {
        params: params,
        ...getRequestConfig(),
      }
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching signal events:', error);
    return {
      success: false,
      message: error.response?.data?.error || 'Failed to fetch signal events',
      error: error.response?.data || error.message,
    };
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Validate strategy configuration before submission
 * @param {Object} strategyData - Strategy configuration to validate
 * @returns {Object} Validation result with errors if any
 */
export const validateStrategyConfig = (strategyData) => {
  const errors = [];

  if (!strategyData.strategy_id) {
    errors.push('Strategy ID is required');
  }

  if (!strategyData.symbol) {
    errors.push('Symbol is required');
  }

  if (!strategyData.timeframe) {
    errors.push('Timeframe is required');
  }

  if (!strategyData.conditions || strategyData.conditions.length === 0) {
    errors.push('At least one condition is required');
  }

  if (strategyData.conditions) {
    strategyData.conditions.forEach((condition, index) => {
      if (!condition.name && !condition.signal_name) {
        errors.push(`Condition ${index + 1} must have a name`);
      }

      if (!condition.type) {
        errors.push(`Condition ${index + 1} must have a type`);
      }

      if (['time_bound', 'interference', 'multiple_targets'].includes(condition.type)) {
        if (!condition.trigger) {
          errors.push(`Condition ${index + 1} (${condition.type}) must have a trigger`);
        }
        if (!condition.target) {
          errors.push(`Condition ${index + 1} (${condition.type}) must have a target`);
        }
      }

      if (condition.type === 'simultaneous') {
        if (!condition.signal1) {
          errors.push(`Condition ${index + 1} (simultaneous) must have signal1`);
        }
        if (!condition.signal2) {
          errors.push(`Condition ${index + 1} (simultaneous) must have signal2`);
        }
      }
    });
  }

  if (!strategyData.entry_logic || !['all', 'any'].includes(strategyData.entry_logic)) {
    errors.push('Entry logic must be "all" or "any"');
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted time string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
      return `${diffSecs} seconds ago`;
    } else if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

/**
 * Get condition type label
 * @param {string} type - Condition type
 * @returns {string} Human-readable label
 */
export const getConditionTypeLabel = (type) => {
  const labels = {
    time_bound: 'Time Bound',
    interference: 'Interference',
    simultaneous: 'Simultaneous',
    multiple_targets: 'Multiple Targets',
  };
  return labels[type] || type;
};

/**
 * Parse signal direction from signal data
 * @param {Object} signalData - Signal data
 * @returns {string} Direction (LONG/SHORT/BUY/SELL/SIGNAL)
 */
export const parseSignalDirection = (signalData) => {
  if (!signalData) return 'SIGNAL';

  // Check if it's an entry signal
  if (signalData.condition_type === 'entry') {
    return 'ENTRY';
  }

  // Try to get direction from trigger metadata
  if (signalData.trigger_metadata?.direction) {
    return signalData.trigger_metadata.direction.toUpperCase();
  }

  // Try to get direction from target metadata
  if (signalData.target_metadata?.direction) {
    return signalData.target_metadata.direction.toUpperCase();
  }

  // Try to get direction from signal1 or signal2
  if (signalData.signal1_metadata?.direction) {
    return signalData.signal1_metadata.direction.toUpperCase();
  }

  if (signalData.signal2_metadata?.direction) {
    return signalData.signal2_metadata.direction.toUpperCase();
  }

  return 'SIGNAL';
};

// Default export with all functions
const strategyService = {
  // Dashboard
  getDashboardStats,
  getStrategyPerformance,
  
  // Strategy Management
  setupStrategy,
  listStrategies,
  getStrategyState,
  resetStrategy,
  deleteStrategy,
  
  // Signal Processing
  sendSignalAlert,
  
  // History and Logs
  getDetectedSignals,
  getSignalEvents,
  
  // Utilities
  validateStrategyConfig,
  formatTimestamp,
  getConditionTypeLabel,
  parseSignalDirection,
};

export default strategyService;
